import type { Translated } from '@uefadigital/panenka';
import { PkIcon } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { memo, useCallback } from 'react';

import { useVideoPlaylistOptions, useVideoPlaylistStore } from '../../store';

import { useTeams } from '@hooks/useTeams';
import { useWindowSize } from '@hooks/useWindowSize';
import TeamIdentifier from '@shared/Identifier/TeamIdentifier';
import ListFilterModal from '@shared/ListFilterModal';
import { useTranslateFn } from '@shared/Translate';
import type { Team } from '@shared-types/match.types';

import * as classes from './styles.module.scss';

const ItemComponent = memo(
  ({
    entity: team,
    selected,
    onSelect,
  }: {
    entity: Team;
    onSelect: (selected: boolean) => void;
    selected: boolean;
  }) => {
    const handleSelect = (
      e: CustomEvent<{
        element: HTMLElement;
        selected: boolean;
      }>,
    ) => onSelect(e.detail.selected);

    return (
      <TeamIdentifier
        key={`team-${team.id}`}
        className={clsx('pk-py--xs', classes.identifier)}
        team={team}
        type='vertical'
        enableSelection
        showTeamCountry={false}
        selected={selected}
        onIdentifierSelected={handleSelect}
      />
    );
  },
);
ItemComponent.displayName = 'ItemComponent';

const TeamFilter = () => {
  const t = useTranslateFn();
  const { isMobile } = useWindowSize();
  const { options } = useVideoPlaylistOptions();
  const selectedTeam = useVideoPlaylistStore((state) => state.filters?.team);
  const setTeam = useVideoPlaylistStore((state) => state.setTeam);
  const buttonLabel = t(window.isClub ? 'club' : 'team');

  const teams = useTeams({
    competitionId: window.competitionId,
    seasonYear: options.season ?? 'current',
    phase: options.phase,
  });

  const handleSelect = useCallback(
    (teams: Translated<Team>[]) => {
      setTeam(teams?.at(0));
    },
    [setTeam],
  );

  const renderButton = () => (
    <>
      {(isMobile || !selectedTeam) && (
        <>
          {buttonLabel}
          <PkIcon className='pk-ml--xs' name='actions-filter' size={24} />
        </>
      )}
      {!isMobile && selectedTeam && selectedTeam.displayName}
    </>
  );

  return (
    <ListFilterModal
      headerText={buttonLabel}
      buttonClassName={classes.button}
      ButtonContent={renderButton}
      onSelected={handleSelect}
      entities={teams}
      selectedEntities={selectedTeam ? [selectedTeam] : []}
      ItemComponent={ItemComponent}
    />
  );
};

export default memo(TeamFilter);
